/**
 * Character Message Panel Component
 * Displays stage-specific character messages contextually
 */

import React, { useState, useEffect, useRef } from 'react';
import { PlayerRole } from '../models/Player';
import { StageId } from '../models/GameState';
import { contentLoader } from '../content';
import { CharacterMessage } from '@/data/gameGuideContent';
import { IoHome, IoJournal, IoCloudOutline, IoEye, IoChatbubble, IoMail } from 'react-icons/io5';
import { FaBrain } from 'react-icons/fa';

interface CharacterMessagePanelProps {
  stage: StageId;
  role: PlayerRole;
  timing?: 'immediate' | 'delayed' | 'afterQuestion';
  className?: string;
}

export const CharacterMessagePanel: React.FC<CharacterMessagePanelProps> = ({
  stage,
  role,
  timing = 'immediate',
  className = ''
}) => {
  const [messages, setMessages] = useState<CharacterMessage[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [readMessages, setReadMessages] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  useEffect(() => {
    // Load messages for current stage and role
    const stageMessages = contentLoader.getStageCharacterMessages(stage, role, timing);
    setMessages(stageMessages);
    setExpandedIndex(null);
  }, [stage, role, timing]);

  // Check if content is scrollable and update indicator visibility
  useEffect(() => {
    const checkScrollable = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const isScrollable = container.scrollHeight > container.clientHeight;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 5;

      setShowScrollIndicator(isScrollable && !isAtBottom);
    };

    checkScrollable();
    const container = scrollContainerRef.current;

    if (container) {
      container.addEventListener('scroll', checkScrollable);
      window.addEventListener('resize', checkScrollable);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollable);
      }
      window.removeEventListener('resize', checkScrollable);
    };
  }, [messages, expandedIndex]);

  const getIconEmoji = (icon: string): React.ReactNode => {
    // PNG icons for main characters
    const pngIcons: Record<string, string> = {
      'mothership': '/icons/MOTHERSHIP.png',
      'gee': '/icons/GEE.png',
      'doc': '/icons/doc.png'
    };

    if (pngIcons[icon]) {
      return <img src={pngIcons[icon]} alt={icon} className="w-8 h-8 object-contain" />;
    }

    // React icons for other message types
    const iconMap: Record<string, React.ReactNode> = {
      'home': <IoHome />,
      'journal': <IoJournal />,
      'memory': <FaBrain />,
      'conscience': <IoCloudOutline />,
      'seeing': <IoEye />
    };
    return iconMap[icon] || <IoChatbubble />;
  };

  const getIconLabel = (icon: string): string => {
    const labelMap: Record<string, string> = {
      'mothership': 'Mothership',
      'home': 'Message from Home',
      'gee': 'GEE',
      'doc': 'DOC',
      'journal': 'Research Journal',
      'memory': 'Memory',
      'conscience': 'Conscience',
      'seeing': 'Observation'
    };
    return labelMap[icon] || icon;
  };

  const handleToggleMessage = (index: number) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
      // Mark as read
      const messageId = `${stage}-${messages[index].icon}-${index}`;
      setReadMessages(prev => new Set([...prev, messageId]));
    }
  };

  const isMessageRead = (index: number): boolean => {
    const messageId = `${stage}-${messages[index].icon}-${index}`;
    return readMessages.has(messageId);
  };

  const unreadCount = messages.filter((_, idx) => !isMessageRead(idx)).length;

  if (messages.length === 0) {
    return null;
  }

  return (
    <div className={`bg-white border border-[#CED4DE] rounded-xl shadow-sm ${className} relative`}>
      <div className="px-4 py-3 border-b border-[#CED4DE] flex items-center justify-between">
        <h3 className="text-base font-bold text-[#12356B] flex items-center gap-2">
          <IoMail /> Character Messages
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#DD6832] rounded-full">
              {unreadCount}
            </span>
          )}
        </h3>
      </div>
      <div ref={scrollContainerRef} className="divide-y divide-[#E2E5EC] max-h-96 overflow-y-auto scrollbar-thin">
        {messages.map((message, index) => {
          const isExpanded = expandedIndex === index;
          const isRead = isMessageRead(index);
          const emoji = getIconEmoji(message.icon);
          const label = getIconLabel(message.icon);

          return (
            <div
              key={index}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                isExpanded ? 'bg-[#E2E5EC]' : isRead ? 'bg-white' : 'bg-[#DD6832]/8'
              } hover:bg-[#E2E5EC]/60`}
              onClick={() => handleToggleMessage(index)}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-2xl flex-shrink-0">{emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#12356B] flex items-center gap-2">
                      {label}
                      {!isRead && (
                        <span className="inline-block w-2 h-2 bg-[#DD6832] rounded-full"></span>
                      )}
                    </div>
                    {!isExpanded && (
                      <div className="text-xs text-gray-600 truncate mt-0.5">
                        {message.message.substring(0, 60)}...
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-slate-500 text-sm flex-shrink-0">
                  {isExpanded ? '▼' : '▶'}
                </span>
              </div>
              {isExpanded && (
                <div className="mt-3 pl-11 text-sm text-slate-700 leading-relaxed max-h-40 overflow-y-auto scrollbar-thin">
                  {message.message}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none bg-gradient-to-t from-white via-white/80 to-transparent rounded-b-xl flex items-end justify-center pb-2">
          <div className="flex items-center gap-1 text-xs text-gray-500 font-medium">
            <span>Scroll for more</span>
            <svg className="w-3 h-3 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterMessagePanel;
